import { format } from 'date-fns';

import { API_URL } from '../config/api';
import apiClient from '../store/apiClient';
import HttpError from './providerHelpers/HttpError';

const pqfDocumentPath = (companyId, pqfId) => `companies/${companyId}/pqf/${pqfId}/download`;

const parseFilenameFromContentDisposition = header => {
  if (!header || typeof header !== 'string') return null;
  const encoded = /filename\*=(?:UTF-8'')?([^;\n]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].replace(/^"+|"+$/g, '').trim());
    } catch (e) {
      return encoded[1].replace(/^"+|"+$/g, '').trim();
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted) return quoted[1];
  const plain = /filename=([^;\n]+)/i.exec(header);
  if (plain) return plain[1].replace(/^"+|"+$/g, '').trim();
  return null;
};

const saveBlobAsFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** Same header as other file downloads in {@link ./providerHelpers/fetchUtils}. */
const resolveDownloadFilename = (response, pqfId) => {
  const fileNameHeader = response.headers.get('file-name');
  if (fileNameHeader) {
    const fromFileNameHeader = decodeURI(fileNameHeader)?.replaceAll(
      '{datetime}',
      format(new Date(), 'MM-dd-yyyy-HH-mm-ss'),
    );
    if (fromFileNameHeader) return fromFileNameHeader;
  }

  const fromContentDisposition = parseFilenameFromContentDisposition(
    response.headers.get('Content-Disposition'),
  );
  if (fromContentDisposition) return fromContentDisposition;

  return `pqf-${pqfId}.pdf`;
};

/**
 * Downloads the stored PQF document, reading the response body as a blob
 * regardless of the returned content-type.
 * @throws {HttpError}
 */
export const downloadPqfDocument = async ({ companyId, pqfId }) => {
  const url = `${API_URL.replace(/\/$/, '')}/${pqfDocumentPath(companyId, pqfId)}`;

  const response = await apiClient(
    url,
    {
      method: 'GET',
      headers: new Headers({ Accept: 'application/pdf' }),
    },
    false,
  );

  const blob = await response.blob();
  if (!blob || blob.size === 0) {
    throw new HttpError('PQF document is empty or not available.', 404);
  }

  const filename = resolveDownloadFilename(response, pqfId);
  saveBlobAsFile(blob, filename);
};
